import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, FileText, AlertTriangle, Scale, TrendingUp,
  Briefcase, Users, Info, ChevronRight, ExternalLink
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DisclosuresPage = () => {
  const [activeSection, setActiveSection] = useState('');

  // Table of contents sections
  const sections = [
    { id: 'no-offer', title: 'No Offer or Solicitation', icon: FileText },
    { id: 'accredited', title: 'Accredited Investor Requirement', icon: Users },
    { id: 'risks', title: 'Investment Risks', icon: AlertTriangle },
    { id: 'forward-looking', title: 'Forward-Looking Statements', icon: TrendingUp },
    { id: 'regulatory', title: 'Regulatory Compliance', icon: Scale },
    { id: 'no-advice', title: 'No Financial, Legal, or Tax Advice', icon: Briefcase },
    { id: 'accuracy', title: 'Website Information Accuracy', icon: Info }
  ];

  // Track scroll position for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Investment and Legal Disclosures | ASAP Capital</title>
        <meta 
          name="description" 
          content="Important investment disclosures, risk factors, and legal information for ASAP Capital's private equity real estate offerings." 
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="/disclosures" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-12 lg:pt-44 lg:pb-16 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]" />
            
            <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-white/5 border border-white/10">
                  <Shield className="w-4 h-4 text-white/60" />
                  <span className="text-xs font-medium text-white/60 uppercase tracking-wider">Legal</span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-6">
                  Investment and Legal Disclosures
                </h1>

                <div className="text-white/60 text-base lg:text-lg leading-relaxed space-y-4">
                  <p>
                    The information provided on this website is for general informational purposes only 
                    and is not intended to constitute an offer to sell, or a solicitation of an offer 
                    to buy, any securities. All investment opportunities, if any, are offered only 
                    through official offering documents and to qualified investors in accordance with 
                    applicable securities laws.
                  </p>
                  <p>
                    Visitors are encouraged to review the disclosures below carefully before making 
                    any investment-related decisions.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MAIN CONTENT WITH TOC ==================== */}
          <section className="py-12 lg:py-16 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <div className="grid lg:grid-cols-[280px_1fr] gap-12">
                
                {/* Sticky Table of Contents - Desktop Only */}
                <div className="hidden lg:block">
                  <div className="sticky top-32">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-4">On This Page</p>
                    <nav className="space-y-1">
                      {sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                            activeSection === section.id
                              ? 'bg-white/10 text-white border-l-2 border-[#D4AF37]'
                              : 'text-white/50 hover:text-white/70 hover:bg-white/5'
                          }`}
                        >
                          <section.icon className="h-4 w-4 flex-shrink-0" />
                          <span className="line-clamp-1">{section.title}</span>
                        </button>
                      ))}
                    </nav>

                    <div className="mt-8 pt-6 border-t border-white/10">
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Related Pages</p>
                      <div className="space-y-2">
                        <Link 
                          to="/terms"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Terms & Conditions
                        </Link>
                        <Link 
                          to="/privacy"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Privacy Policy
                        </Link>
                        <Link 
                          to="/contact"
                          className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                          Contact Us
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="max-w-[700px]">
                  {/* No Offer or Solicitation */}
                  <section id="no-offer" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <FileText className="h-6 w-6 text-[#D4AF37]" />
                        No Offer or Solicitation
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          Nothing on this website constitutes an offer to sell or a solicitation of an 
                          offer to buy any security or investment product. Any such offer or solicitation 
                          will be made only by means of a confidential Private Placement Memorandum ("PPM") 
                          and related subscription documents and only to persons who meet applicable 
                          investor suitability standards.
                        </p>
                        <p>
                          Investment opportunities, if any, are offered only in jurisdictions where lawful 
                          and only to persons who qualify under applicable securities regulations.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Accredited Investor Requirement */}
                  <section id="accredited" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Users className="h-6 w-6 text-[#D4AF37]" />
                        Accredited Investor Requirement
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          Certain investment opportunities described on this website may be available only 
                          to individuals and entities that qualify as "investors" under Rule 501 
                          of Regulation D of the Securities Act of 1933, as amended.
                        </p>
                        <p>Generally, investors include:</p>
                        <ul className="list-disc list-inside space-y-2 ml-2">
                          <li>
                            Individuals with income exceeding $200,000 in each of the two most recent years 
                            (or $300,000 jointly with a spouse), with a reasonable expectation of the same 
                            income level in the current year; or
                          </li>
                          <li>
                            Individuals with a net worth exceeding $1,000,000, excluding the value of their 
                            primary residence; or
                          </li>
                          <li>
                            Certain institutional investors, trusts, and entities meeting regulatory standards.
                          </li>
                        </ul>
                        <p>
                          Eligibility requirements are subject to verification as part of any investment process.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Investment Risks */}
                  <section id="risks" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <AlertTriangle className="h-6 w-6 text-[#D4AF37]" />
                        Investment Risks
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          All real estate and private equity investments involve significant risks, including 
                          the possible loss of principal. Prospective investors should carefully consider, 
                          among others, the following risks:
                        </p>
                        <ul className="space-y-4">
                          <li>
                            <strong className="text-white">Market Risk:</strong> Changes in economic conditions, 
                            interest rates, and local market dynamics may adversely affect property performance.
                          </li>
                          <li>
                            <strong className="text-white">Liquidity Risk:</strong> Investments may be illiquid, 
                            and investors may not be able to sell or transfer interests readily.
                          </li>
                          <li>
                            <strong className="text-white">Leverage Risk:</strong> Use of debt can magnify both 
                            gains and losses and increase financial risk.
                          </li>
                          <li>
                            <strong className="text-white">Operational Risk:</strong> Property operations, tenant 
                            demand, and management execution may not meet expectations.
                          </li>
                          <li>
                            <strong className="text-white">Regulatory Risk:</strong> Changes in laws, zoning, or 
                            tax regulations may impact investment outcomes.
                          </li>
                        </ul>
                        <p>
                          Additional risks are described in detail in official offering documents.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Forward-Looking Statements */}
                  <section id="forward-looking" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <TrendingUp className="h-6 w-6 text-[#D4AF37]" />
                        Forward-Looking Statements
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          This website may contain forward-looking statements, including statements regarding 
                          expectations, objectives, strategies, or future performance. These statements are 
                          based on current assumptions and are subject to risks and uncertainties that could 
                          cause actual results to differ materially from those anticipated.
                        </p>
                        <p>
                          <strong className="text-white">Past performance is not indicative of future results.</strong> No 
                          assurance can be given that any investment objectives will be achieved.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Regulatory and Securities Law Compliance */}
                  <section id="regulatory" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Scale className="h-6 w-6 text-[#D4AF37]" />
                        Regulatory and Securities Law Compliance
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          Any securities offerings, if made, are intended to be exempt from registration under 
                          the Securities Act of 1933 pursuant to Regulation D and applicable state securities 
                          laws. Such offerings are not registered with the U.S. Securities and Exchange 
                          Commission or with any state securities authority.
                        </p>
                        <p>
                          This website does not provide complete information regarding any investment offering 
                          and should not be relied upon in connection with any investment decision.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* No Financial, Legal, or Tax Advice */}
                  <section id="no-advice" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Briefcase className="h-6 w-6 text-[#D4AF37]" />
                        No Financial, Legal, or Tax Advice
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          Nothing on this website should be construed as financial, legal, or tax advice. 
                          Prospective investors should consult with their own independent financial advisors, 
                          attorneys, and tax professionals before making any investment decisions.
                        </p>
                        <p>
                          Each investor's financial situation and risk tolerance are unique, and investment 
                          decisions should be based on individual circumstances.
                        </p>
                      </div>
                    </motion.div>
                  </section>

                  {/* Website Information Accuracy */}
                  <section id="accuracy" className="mb-12">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 flex items-center gap-3">
                        <Info className="h-6 w-6 text-[#D4AF37]" />
                        Website Information Accuracy
                      </h2>
                      <div className="text-white/60 leading-relaxed space-y-4">
                        <p>
                          While reasonable efforts are made to ensure that the information on this website 
                          is accurate and current, no representations or warranties are made regarding 
                          completeness, reliability, or accuracy.
                        </p>
                        <p>
                          Information on this website is subject to change without notice and may not reflect 
                          the most current developments or offerings.
                        </p>
                      </div>
                    </motion.div>
                  </section>
                </div>
              </div>
            </div>
          </section>

          {/* ==================== LEGAL FOOTER NOTE ==================== */}
          <section className="py-12 relative border-t border-white/5">
            <div className="max-w-[900px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-6"
              >
                <div className="flex items-start gap-4">
                  <Shield className="h-6 w-6 text-white/30 flex-shrink-0 mt-1" />
                  <div className="text-white/40 text-sm leading-relaxed">
                    <p className="mb-4">
                      This website is operated by ASAP Capital. All investments involve risk, including 
                      the possible loss of principal. Investment opportunities are available only through 
                      official offering documents and only to qualified investors. Please refer to the 
                      applicable Private Placement Memorandum for complete investment terms and disclosures.
                    </p>
                    <p className="text-white/30 text-xs">
                      Last updated: January 2026
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== RELATED LINKS ==================== */}
          <section className="py-8 relative">
            <div className="max-w-[900px] mx-auto px-6 lg:px-12">
              <div className="flex flex-wrap justify-center gap-4">
                <Link 
                  to="/terms"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Terms & Conditions
                </Link>
                <Link 
                  to="/privacy"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Privacy Policy
                </Link>
                <Link 
                  to="/contact"
                  className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default DisclosuresPage;
